/**
 * Subscription helpers for gating access by school.
 *
 * Notes:
 * - We treat dates as DATE-only (no time) to avoid timezone edge cases.
 * - If subscription dates are not set, we treat the school as "legacy active".
 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function formatDateToISODate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function parseISODateOnly(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function toDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function addMonthsDateOnly(date, months) {
  const d = toDateOnly(date);
  if (!d) return null;
  const m = Number(months);
  if (!Number.isFinite(m)) return null;

  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const day = d.getDate();

  const targetMonthIndex = monthIndex + m;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const normalizedTargetMonthIndex = ((targetMonthIndex % 12) + 12) % 12;
  const dim = daysInMonth(targetYear, normalizedTargetMonthIndex);
  const clampedDay = Math.min(day, dim);

  return new Date(targetYear, normalizedTargetMonthIndex, clampedDay);
}

export function addMonthsToISODate(startISO, months) {
  const start = parseISODateOnly(startISO);
  if (!start) return '';
  const end = addMonthsDateOnly(start, months);
  return formatDateToISODate(end) || '';
}

/**
 * Evaluate a school's subscription and return a normalized status.
 *
 * Expected school fields (from `schools` table):
 * - subscription_start_date (date string: YYYY-MM-DD)
 * - subscription_end_date (date string: YYYY-MM-DD)
 * - subscription_paid (boolean)
 * - subscription_warning_days (int)
 */
export function evaluateSchoolSubscription(school, now = new Date()) {
  const warningDaysRaw = school?.subscription_warning_days;
  const warningDays = Number.isFinite(Number(warningDaysRaw)) ? Math.max(0, Number(warningDaysRaw)) : 7;

  const paidRaw = school?.subscription_paid;
  const paid = paidRaw === undefined || paidRaw === null ? true : Boolean(paidRaw);

  const start = parseISODateOnly(school?.subscription_start_date);
  const end = parseISODateOnly(school?.subscription_end_date);
  const today = toDateOnly(now) || new Date();

  // Legacy behavior: no dates configured => allow access (but still honor paid if set explicitly false).
  const hasAnyDates = Boolean(start) || Boolean(end);
  if (!hasAnyDates) {
    if (!paid) {
      return {
        status: 'unpaid',
        isAccessible: false,
        message: 'Account Currently Unavailable',
        warningDays,
        startDate: null,
        endDate: null,
        daysRemaining: null
      };
    }
    return {
      status: 'legacy',
      isAccessible: true,
      message: '',
      warningDays,
      startDate: null,
      endDate: null,
      daysRemaining: null
    };
  }

  if (!paid) {
    return {
      status: 'unpaid',
      isAccessible: false,
      message: 'Account Currently Unavailable',
      warningDays,
      startDate: start,
      endDate: end,
      daysRemaining: end ? Math.ceil((end.getTime() - today.getTime()) / MS_PER_DAY) : null
    };
  }

  if (start && today.getTime() < start.getTime()) {
    return {
      status: 'not_started',
      isAccessible: false,
      message: 'Account Currently Unavailable',
      warningDays,
      startDate: start,
      endDate: end,
      daysRemaining: end ? Math.ceil((end.getTime() - today.getTime()) / MS_PER_DAY) : null
    };
  }

  if (end && today.getTime() > end.getTime()) {
    return {
      status: 'expired',
      isAccessible: false,
      message: 'Account Currently Unavailable',
      warningDays,
      startDate: start,
      endDate: end,
      daysRemaining: Math.ceil((end.getTime() - today.getTime()) / MS_PER_DAY)
    };
  }

  const daysRemaining = end ? Math.ceil((end.getTime() - today.getTime()) / MS_PER_DAY) : null;
  if (end && daysRemaining !== null && daysRemaining <= warningDays) {
    return {
      status: 'expiring',
      isAccessible: true,
      message: '',
      warningDays,
      startDate: start,
      endDate: end,
      daysRemaining
    };
  }

  return {
    status: 'active',
    isAccessible: true,
    message: '',
    warningDays,
    startDate: start,
    endDate: end,
    daysRemaining
  };
}


